import React, { useState, useMemo, useEffect } from "react";
import styles from "./PsrAlignmentCalibrationComponent.module.css";
import LayoutWithLog from "../LayoutWithLog";
import CommonDropDown, {
  CommonDropDownDisplayValue,
} from "../../../Common/DropDown/CommonDropDown";
import CheckBox from "../../../Buttons/CheckBox";
import MtiButton from "../../../Buttons/MtiButton";
import { PsrAlignmentPointResult } from "../../../../Model/PsrAlignmentCalibrationResult";
import { Column } from "react-table";
import CommonTable from "../../../Common/CommonTable";
import PsrAlignmentCalibrationResult from "../../../../Model/PsrAlignmentCalibrationResult";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { ESuiteMode, EPsrState } from "../../../../Model/ModuleState";
import CalibrationApi from "../../../../api/CalibrationApi";
import { ERi1000PsrAlignmentLocation } from "../../../../Model/PsrAlignmentCalibrationSetting";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../../../redux/actions/NotificationAction";
import { ENotificationType } from "../../../../Model/ENotificationType";
import { ECommonProcessState } from "../../../../Model/ECommonProcessState";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../../Model/EModuleType";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import usePersistState from "../../../../Utility/usePersistState";
import useCalibrationLog from "../../../Hooks/useCalibrationLog";
import { calibrationSignalRMap } from "../SettingsStorageRi1000";

interface Prop {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  setIsCalibrationUpdated: (isUpdated: boolean) => void;
  setResetHandler: (onReset: {
    execute: () => void;
    disabled: boolean;
  }) => void;
  setRunningTabKey: React.Dispatch<React.SetStateAction<string>>;
}

export default function PsrAlignmentCalibrationComponent({
  setOnApply,
  setOnCancel,
  setIsCalibrationUpdated,
  setResetHandler,
  setRunningTabKey,
}: Prop) {
  const dispatch = useDispatch();
  const [processState, setProcessState] = usePersistState(
    ECommonProcessState.Initial,
    "PSR_ALIGNMENT_CALIBRATION_PROCESS_STATE"
  );
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrIdentifier = psrModules?.[0]?.identifier;
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrIdentifier).psr
  );

  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.Error;

  // GET LOG LOGIC
  const [logs] = useCalibrationLog(calibrationSignalRMap.psrAlignment);

  useEffect(() => {
    if (suiteMode == ESuiteMode.AutoCalibrating) {
      setProcessState(ECommonProcessState.Processing);
      setAllowRunCalibration(false);
      return;
    }

    if (suiteMode == ESuiteMode.Normal) {
      if (processState === ECommonProcessState.Processing) {
        if (psrState?.state === EPsrState.Fail) {
          setProcessState(ECommonProcessState.Fault);
          setRunningTabKey("");
        } else if (psrState?.state === EPsrState.Available) {
          setProcessState(ECommonProcessState.Finished);
          setRunningTabKey("");
        }
      }
      setAllowRunCalibration(true);
      return;
    }

    if (suiteMode == ESuiteMode.ArmCrashed || suiteMode == ESuiteMode.Error) {
      setProcessState(ECommonProcessState.Initial);
      return;
    }
  }, [suiteMode, suiteState]);

  const runCalibration = () => {
    setRunningTabKey(calibrationSignalRMap.psrAlignment);
    CalibrationApi.runPsrAlignmentCalibration({
      testLocations: [selectedPosition],
      includeZ: isIncludeZ,
      rotation: parseFloat(rotation),
    }).then(() => {
      setProcessState(ECommonProcessState.Started);
    });
  };

  const stopCalibration = () => {
    CalibrationApi.stopPsrAlignmentCalibration();
  };

  const onApply = () => {
    CalibrationApi.savePsrAlignmentCalibration()
      .then(() => {
        dispatch(
          sendNotification(
            ENotificationType.Success,
            "The changes have been saved successfully"
          )
        );
        setCalibrationState(false);
        setProcessState(ECommonProcessState.Initial);
      })
      .catch(() => {
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "Saving changes encountered an error"
          )
        );
      });
  };

  const onCancel = () => {
    CalibrationApi.revertPsrAlignmentCalibration().then(() => {
      CalibrationApi.getPsrAlignmentCalibraionResult().then((result) => {
        setPsrAlignmentResults(result);
      });
      setCalibrationState(false);
      setProcessState(ECommonProcessState.Initial);
    });
  };

  useEffect(() => {
    setResetHandler({
      execute: () => {
        setIsCalibrationUpdated(false);
        setProcessState(ECommonProcessState.Initial);
        CalibrationApi.getPsrAlignmentCalibraionResult().then((result) => {
          setPsrAlignmentResults(result);
        });
      },
      disabled: false,
    });
  }, []);

  const setCalibrationState = (isUpdated: boolean) => {
    setOnApply({
      execute: onApply,
      disabled: !isUpdated,
    });
    setOnCancel({
      execute: onCancel,
      disabled: !isUpdated,
    });
    setIsCalibrationUpdated(isUpdated);
  };

  const [rotation, setRotation] = useState(Number(108.2).toFixed(2));
  const [tAdjust, setTAdjust] = useState(Number(0).toFixed(2));
  const [eAdjust, setEAdjust] = useState(Number(0).toFixed(2));
  const [zRangeAdjust, setzRangeAdjust] = useState(Number(0).toFixed(2));
  const [isIncludeZ, setIsIncludeZ] = useState(false);
  const [positionSelection, setPositionSelection] = useState([
    {
      display: ERi1000PsrAlignmentLocation[ERi1000PsrAlignmentLocation.Bottom],
      value: ERi1000PsrAlignmentLocation.Bottom,
      disabled: false,
    },
    {
      display: ERi1000PsrAlignmentLocation[ERi1000PsrAlignmentLocation.Top],
      value: ERi1000PsrAlignmentLocation.Top,
      disabled: false,
    },
  ] as CommonDropDownDisplayValue<number>[]);
  const [selectedPosition, setSelectedPosition] = useState(0);

  const [psrAlignmentResults, setPsrAlignmentResults] = useState(
    [] as PsrAlignmentCalibrationResult[]
  );
  const [psrAlignmentPointResult, setPsrAlignmentPointResult] = useState(
    [] as PsrAlignmentPointResult[]
  );

  const resultColumn: Column<PsrAlignmentPointResult>[] = useMemo(
    () =>
      [
        {
          id: "point-name",
          Header: "Point Name",
          accessor: (row: PsrAlignmentPointResult) => row.name,
          Cell: (cell: any) => (
            <div style={{ textAlign: "center" }}>{cell.value}</div>
          ),
        },
        {
          id: "distance",
          Header: "Distance (mm)",
          accessor: (row: PsrAlignmentPointResult) =>
            roundToTwoDecimal(row.distance),
          Cell: (cell: any) => (
            <div style={{ textAlign: "right" }}>{cell.value}</div>
          ),
        },
        {
          id: "arm-theta",
          Header: "Arm Theta (deg)",
          accessor: (row: PsrAlignmentPointResult) =>
            roundToTwoDecimal(row.armTheta),
          Cell: (cell: any) => (
            <div style={{ textAlign: "right" }}>{cell.value}</div>
          ),
        },
        {
          id: "graph-theta",
          Header: "Graph Theta (deg)",
          accessor: (row: PsrAlignmentPointResult) =>
            roundToTwoDecimal(row.adjustedTheta),
          Cell: (cell: any) => (
            <div style={{ textAlign: "right" }}>{cell.value}</div>
          ),
        },
        {
          id: "z",
          Header: "Z (mm)",
          accessor: (row: PsrAlignmentPointResult) => roundToTwoDecimal(row.z),
          Cell: (cell: any) => (
            <div style={{ textAlign: "right" }}>{cell.value}</div>
          ),
        },
      ] as Column<PsrAlignmentPointResult>[],
    []
  );

  useEffect(() => {
    switch (processState) {
      case ECommonProcessState.Finished:
        CalibrationApi.getPsrAlignmentCalibraionResult().then((result) => {
          setPsrAlignmentResults(result);
          setCalibrationState(true);
        });
        break;
      default:
        break;
    }
  }, [processState]);

  const [allowRunCalibration, setAllowRunCalibration] = useState(true);

  useEffect(() => {
    let selectedResult = psrAlignmentResults.filter(
      (result) => result.location == selectedPosition
    );
    if (selectedResult.length > 0) {
      setPsrAlignmentPointResult(selectedResult[0].alignments);
      setTAdjust(Number(selectedResult[0].adjustment.t).toFixed(2));
      setEAdjust(Number(selectedResult[0].adjustment.e).toFixed(2));
      setzRangeAdjust(Number(selectedResult[0].adjustment.zRange).toFixed(2));
    } else {
      setPsrAlignmentPointResult([]);
    }
  }, [selectedPosition, psrAlignmentResults]);

  useEffect(() => {
    CalibrationApi.getPsrAlignmentCalibraionResult().then((result) => {
      setPsrAlignmentResults(result);
    });
  }, []);

  return (
    <div className={styles["outer-border"]}>
      <LayoutWithLog logMessages={logs || []}>
        <LayoutWithLog.Content>
          <div className={styles["calibration-view"]}>
            <div className={styles["calibration-settings"]}>
              <div className={styles["position-selector"]}>
                <div className={styles["label-standard"]}>Position</div>
                <CommonDropDown
                  id="position-selection"
                  style={{ width: "185px" }}
                  value={selectedPosition}
                  dropDownDisplayValues={positionSelection}
                  onChange={(pos) => {
                    setSelectedPosition(pos);
                  }}
                />
              </div>

              <div className={styles["rotation-setter"]}>
                <div className={styles["label-standard"]}>Rotation</div>
                <div className={styles["input-box"]}>
                  <input
                    type="number"
                    value={rotation}
                    onInput={(e) => {
                      let val =
                        e.currentTarget.value === ""
                          ? Number(0).toFixed()
                          : e.currentTarget.value;
                      setRotation(val);
                    }}
                  />
                  <span>deg</span>
                </div>
              </div>

              <div className={styles["include-z-checkbox"]}>
                <div className={styles["label-standard"]}>Include Z</div>
                <CheckBox
                  colorVariant="alternate"
                  name="Include Z Checkbox"
                  id="include-z"
                  checked={isIncludeZ}
                  onChange={(id, value) => setIsIncludeZ(value)}
                />
              </div>
            </div>

            <div className={styles["calibration-control"]}>
              <div className={styles["label"]}>Calibrating PSR Alignment</div>
              <div className={styles["button-group"]}>
                <MtiButton
                  variant="secondary"
                  colorVariant="black"
                  onClick={stopCalibration}
                  disabled={allowRunCalibration}
                >
                  STOP
                </MtiButton>
                <MtiButton
                  variant="primary"
                  colorVariant="black"
                  onClick={runCalibration}
                  disabled={!allowRunCalibration}
                >
                  RUN
                </MtiButton>
              </div>
            </div>

            <div className={styles["calibration-result-view"]}>
              <div className={styles["title"]}>Result</div>
              <div className={styles["arm-adjustment-view"]}>
                <div>Adjust Arm</div>
                <div>
                  T: <span style={{ fontWeight: 500 }}>{tAdjust}</span>
                </div>
                <div>
                  E: <span style={{ fontWeight: 500 }}>{eAdjust}</span>
                </div>
                <div>
                  Z Range:{" "}
                  <span style={{ fontWeight: 500 }}>{zRangeAdjust}</span>
                </div>
              </div>
              <div className={styles["results-table"]}>
                <CommonTable<PsrAlignmentPointResult>
                  data={psrAlignmentPointResult}
                  columns={resultColumn}
                  style={{
                    height: "fit-content",
                    padding: 0,
                  }}
                  variant="no-separator"
                  enableFilter={false}
                  enablePagination={true}
                  enableSelection={false}
                  enableMultiSelect={false}
                  enableManualSorting={false}
                />
              </div>
            </div>
          </div>
        </LayoutWithLog.Content>
      </LayoutWithLog>
    </div>
  );
}

function roundToTwoDecimal(num: number) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}
